import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
    Switch,
    Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import {
    LabelJournalHeaderViewModel,
    LabelJournalLineAddRequest,
    LabelJournalLineFromInventoryRequest,
    LabelJournalPrintRequest,
    LabelTemplateViewModel,
    PageComponentProps
} from "../../Types/ExportTypes";
import { useNavigation } from "../../Context/NavigationContext";
import {
    LocationAutoComplete, CustomerPriceGroupAutoComplete, CurrencyAutoComplete, ItemAutoComplete, CollectionAutoComplete,
    ProgramAutoComplete, ItemGroupAutoComplete, ItemCategoryAutoComplete, ProductGroupAutoComplete
} from "../../components/AutoComplete/AutoCompletes";

import VariantAutoComplete from "../../components/AutoComplete/Item/VariantAutoComplete";
import LabelJournalLines from "../../components/LabelJournal/LabelJournalLines";
import LabelTemplateDialog from "../../components/LabelJournal/LabelTemplateDialog";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";
import DefaultActions from "../../components/Card/DefaultActions"
import { ItemGroup } from "../PageExport";
const defaultAddLineRequest = (documentNo: string): LabelJournalLineAddRequest => ({
    documentNo,
    copies: 1
});

const defaultInventoryRequest = (documentNo: string): LabelJournalLineFromInventoryRequest => ({
    documentNo,
    location: "",
    copies: 1,
    includeVat: true,
});

const LabelJournalEditPage: React.FC<PageComponentProps> = ({ params }) => {
    const documentNo = params?.documentNo as string | undefined;
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const { t } = useTranslation();
    const { goBack, navigate } = useNavigation();

    const [header, setHeader] = useState<LabelJournalHeaderViewModel | null>(null);
    const [printRequest, setPrintRequest] = useState<LabelJournalPrintRequest | null>(null);
    const [addRequest, setAddRequest] = useState<LabelJournalLineAddRequest | null>(null);
    const [inventoryRequest, setInventoryRequest] = useState<LabelJournalLineFromInventoryRequest | null>(null);
    const [templates, setTemplates] = useState<LabelTemplateViewModel[]>([]);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [lineRefreshKey, setLineRefreshKey] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const [showAddRows, setShowAddRows] = useState(false);
    const [showAddFromInventory, setShowAddFromInventory] = useState(false);
    const [showAdvancedControls, setShowAdvancedControls] = useState(false);
    const [showAdvancedPrices, setShowAdvancedPrices] = useState(false);

    const hasPendingChanges = () => header?.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const fetchHeader = useCallback(async () => {
        if (!documentNo) return;
        setIsLoading(true);
        try {
            const response = await authService.get<LabelJournalHeaderViewModel>(`/labelJournalApi/GetLabelJournal?documentNo=${documentNo}`);
            setHeader(
                response
                    ? { ...response, originalValues: { ...response }, hasChanges: false }
                    : null
            )
            setPrintRequest({
                documentNo: response.no,
                templateId: 0,
                priceGroup: response.defaultPriceGroup ?? "",
            });
            setAddRequest(defaultAddLineRequest(response.no));
            setInventoryRequest(defaultInventoryRequest(response.no));
        } catch (error: any) {
            showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    }, [authService, documentNo, showSnackbar, t]);

    const fetchTemplates = useCallback(async () => {
        try {
            const response = await authService.get<{ items: LabelTemplateViewModel[] }>("/labelJournalApi/GetTemplates");
            setTemplates(response.items ?? []);
        } catch (error: any) {
            showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
        }
    }, [authService, showSnackbar, t]);

    useEffect(() => {
        if (!documentNo) return;
        fetchTemplates();
    }, [documentNo, fetchTemplates]);

    useEffect(() => {
        if (!documentNo) return;
        fetchHeader();
    }, [documentNo, fetchHeader]);

    useEffect(() => {
        if (!printRequest || printRequest.templateId) return;
        if (templates.length === 0) return;
        setPrintRequest(prev => prev ? { ...prev, templateId: prev.templateId || templates[0].id } : prev);
    }, [printRequest, templates]);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const templatesOptions = useMemo(() => templates.map(template => ({ value: template.id, label: template.description ?? `${t("common.template")} ${template.id}` })), [templates, t]);

    const updateHeader = async () => {
        if (!header) return;
        showLoading();
        try {
            const updated = await authService.post<LabelJournalHeaderViewModel>("/labelJournalApi/UpdateJournal", header);
            setHeader(updated);
            showSnackbar(t("common.changesSaved"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.saveError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const printJournal = async () => {
        if (!printRequest || !printRequest.templateId) {
            showSnackbar(t("common.selectTemplate"), "warning");
            return;
        }
        showLoading();
        try {
            await authService.post("/labelJournalApi/Print", printRequest);
            showSnackbar(t("common.printQueued"), "success");
        } catch (error: any) {
            showSnackbar(`${t("common.printError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };


    const addLines = async () => {
        if (!addRequest || !addRequest.documentNo) return;
        console.log("addLines", [addRequest, {
            ...addRequest,
            currency: header?.defaultCurrency,
            priceGroup: header?.defaultPriceGroup,
            includeVat: header?.includeVat ?? true,
            location: header?.defaultLocation
        }]);
        showLoading();
        try {
            await authService.post("/labelJournalApi/AddLines", {
                ...addRequest,
                currency: header?.defaultCurrency,
                priceGroup: header?.defaultPriceGroup,
                includeVat: header?.includeVat ?? true,
                location: header?.defaultLocation
            });
            showSnackbar(t("common.added"), "success");
            setAddRequest(defaultAddLineRequest(addRequest.documentNo));
            setLineRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            showSnackbar(`${t("common.addError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };



    const handleKeyDownAddLines = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            addLines();
        }
    };

    const addLinesFromInventory = async () => {
        if (!header?.no) return;
        if (!inventoryRequest?.location) {
            showSnackbar(t("common.locationRequired"), "warning");
     
            return;
        }
        console.log("addLines", [header?.no, inventoryRequest?.location])
        showLoading();
        try {
            await authService.post("/labelJournalApi/AddLinesFromInventory", {
                ...inventoryRequest,
                currency: header?.defaultCurrency,
                priceGroup: header?.defaultPriceGroup,
                includeVat: header?.includeVat ?? true,
                location: inventoryRequest?.location,
                collection: inventoryRequest?.collection,
                program: inventoryRequest?.program,
                itemGroup: inventoryRequest?.itemGroup,
                itemCategory: inventoryRequest?.itemCategory,
                productGroup: inventoryRequest?.productGroup

            });
            showSnackbar(t("common.added"), "success");
            setInventoryRequest(defaultInventoryRequest(header?.no ?? ""));
            setLineRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            showSnackbar(`${t("common.addError")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const clearCopies = async () => {
        if (!header) return;
        try {
            showLoading();
            await authService.post(`/labelJournalApi/ClearLineCopies?documentNo=${header.no}`, {});
            showSnackbar(t("common.copiesCleared"), "success");
            setLineRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            showSnackbar(`${t("common.operationFailed")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const removeAllLines = async () => {
        if (!header) return;
        showLoading();
        try {
            await authService.post(`/labelJournalApi/RemoveAllLines?documentNo=${header.no}`, {});
            showSnackbar(t("common.deleted"), "success");
            setLineRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            showSnackbar(`${t("common.operationFailed")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const deleteJournal = async () => {
        if (!header) return;
        if (!window.confirm(t("dialog.areYouSure"))) return;
        showLoading();
        try {
            await authService.post(`/labelJournalApi/DeleteJournal?documentNo=${header.no}`, {});
            showSnackbar(t("common.deleted"), "success");
            hideLoading();
            navigate({ path: "/inventory/labeljournal/list" });

        } catch (error: any) {
            showSnackbar(`${t("common.operationFailed")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            hideLoading();
        }
    };

    const handleCancelChanges = () => {
        // setIsLoading(true);
        try {
            setHeader((prev) => {
                if (!prev || !prev.originalValues) return prev;
                const original = prev.originalValues;
                const changes: Partial<LabelJournalHeaderViewModel> = {};
                for (const key in original) {
                    const typedKey = key as keyof LabelJournalHeaderViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];
                    if (originalValue !== currentValue) {
                        (changes as any)[typedKey] = originalValue;
                    }
                }
                 if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
                return { ...prev, ...changes, hasChanges: false };
            });
        } finally {
            //  setIsLoading(false);
        }
    };



    if (!documentNo) {
        return (
            <Typography variant="body1" color="error">
                {t("common.missingDocument")}
            </Typography>
        );
    }

    return (
        <CenteredCard
            ref={toolbarRef}
            title={t("common.itemLabels")}
            isLoading={isLoading}
            handleSave={updateHeader}
            handleCancel={handleCancelChanges}
            hasChanges={header?.hasChanges ?? false}
        >
            <Stack spacing={3} sx={{ width: "100%", maxWidth: "1200px" }}>


                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label={t("common.number")}
                                        value={header?.no ?? ""}
                                        fullWidth
                                        disabled
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label={t("common.description")}
                                        value={header?.description ?? ""}
                                        onChange={(e) => setHeader(prev => prev ? { ...prev, description: e.target.value, hasChanges: true } : prev)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomerPriceGroupAutoComplete
                                        key={header?.defaultPriceGroup ?? null}
                                        valueId={header?.defaultPriceGroup ?? null}
                                        onChange={(selected) => setHeader(prev => prev ? { ...prev, defaultPriceGroup: selected?.value ?? "" } : prev)}
                                        selectFirst={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CurrencyAutoComplete
                                        key={header?.defaultCurrency ?? null}
                                        valueId={header?.defaultCurrency ?? null}
                                        onChange={(selected) => setHeader(prev => prev ? { ...prev, defaultCurrency: selected?.value ?? "" } : prev)}
                                        selectFirst={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <LocationAutoComplete
                                        key={header?.defaultLocation ?? null}
                                        valueId={header?.defaultLocation ?? null}
                                        onChange={(selected) => setHeader(prev => prev ? { ...prev, defaultLocation: selected?.value ?? "" } : prev)}
                                        selectFirst={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={<Checkbox checked={header?.includeVat ?? true} onChange={(e) => setHeader(prev => prev ? { ...prev, includeVat: e.target.checked } : prev)} />}
                                        label={t("common.includeVat")}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAdvancedPrices} onChange={(e) => setShowAdvancedPrices(e.target.checked)} />}
                                        label={t("common.showAdvancedPrices")}
                                    />
                                </Grid>

                                {showAdvancedPrices && (
                                    <>
                                        <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 2 }} >
                                            {t("help.labelJournalAdvancedPrices")}
                                        </Alert>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CustomerPriceGroupAutoComplete
                                                label={t("common.priceGroup") + " A"}
                                                key={printRequest?.priceGroupA ?? null}
                                                valueId={printRequest?.priceGroupA ?? null}
                                                onChange={(selected) => setPrintRequest(prev => prev ? { ...prev, priceGroupA: selected?.value ?? "" } : prev)}
                                       
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CurrencyAutoComplete
                                                label={t("common.currency") + " A"}
                                                key={printRequest?.currenciesA ?? null}
                                                valueId={printRequest?.currenciesA ?? null}
                                                onChange={(selected) => setPrintRequest(prev => prev ? { ...prev, currenciesA: selected?.value ?? "" } : prev)}
                                           
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CustomerPriceGroupAutoComplete
                                                label={t("common.priceGroup") + " B"}
                                                key={printRequest?.priceGroupB ?? null}
                                                valueId={printRequest?.priceGroupB ?? null}
                                                onChange={(selected) => setPrintRequest(prev => prev ? { ...prev, priceGroupB: selected?.value ?? "" } : prev)}
                                   
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CurrencyAutoComplete
                                                label={t("common.currency") + " B"}
                                                key={printRequest?.currenciesB ?? null}
                                                valueId={printRequest?.currenciesB ?? null}
                                                onChange={(selected) => setPrintRequest(prev => prev ? { ...prev, currenciesB: selected?.value ?? "" } : prev)}

                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <DefaultActions
                                onDelete={deleteJournal}
                                onSave={updateHeader}
                                customActions={[
                                    { label: t("common.templates"), onClick: () => setTemplateDialogOpen(true) },
                                    { label: t("common.print"), onClick: printJournal }
                                ]}
                            />

                        </Stack>
       
                        <Stack spacing={2}>
                            <Typography variant="h6">{t("common.print")}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        select
                                        label={t("common.template")}
                                        value={printRequest?.templateId ?? ""}
                                        onChange={(e) => setPrintRequest(prev => prev ? { ...prev, templateId: Number(e.target.value) } : prev)}
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    >
                                        <option value="" disabled>{t("common.selectTemplate")}</option>
                                        {templatesOptions.map(option => (
                                            <option key={option.value} value={option.value ?? ""}>{option.label}</option>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label={t("common.copies")}
                                        type="number"
                                        value={header?.copies ?? 0}
                                        onChange={(e) => setHeader(prev => prev ? { ...prev, copies: Number(e.target.value), hasChanges: true } : prev)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                {/*<Grid item xs={12} md={6}>*/}
                                {/*    <TextField*/}
                                {/*        label={t("common.priceGroupA")}*/}
                                {/*        value={printRequest?.priceGroupA ?? ""}*/}
                                {/*        onChange={(e) => setPrintRequest(prev => prev ? { ...prev, priceGroupA: e.target.value } : prev)}*/}
                                {/*        fullWidth*/}
                                {/*    />*/}
                                {/*</Grid>*/}
                                {/*<Grid item xs={12} md={6}>*/}
                                {/*    <TextField*/}
                                {/*        label={t("common.priceGroupB")}*/}
                                {/*        value={printRequest?.priceGroupB ?? ""}*/}
                                {/*        onChange={(e) => setPrintRequest(prev => prev ? { ...prev, priceGroupB: e.target.value } : prev)}*/}
                                {/*        fullWidth*/}
                                {/*    />*/}
                                {/*</Grid>*/}
                                {/*<Grid item xs={12} md={6}>*/}
                                {/*    <TextField*/}
                                {/*        label={t("common.currenciesA")}*/}
                                {/*        value={printRequest?.currenciesA ?? ""}*/}
                                {/*        onChange={(e) => setPrintRequest(prev => prev ? { ...prev, currenciesA: e.target.value } : prev)}*/}
                                {/*        fullWidth*/}
                                {/*    />*/}
                                {/*</Grid>*/}
                                {/*<Grid item xs={12} md={6}>*/}
                                {/*    <TextField*/}
                                {/*        label={t("common.currenciesB")}*/}
                                {/*        value={printRequest?.currenciesB ?? ""}*/}
                                {/*        onChange={(e) => setPrintRequest(prev => prev ? { ...prev, currenciesB: e.target.value } : prev)}*/}
                                {/*        fullWidth*/}
                                {/*    />*/}
                                {/*</Grid>*/}
                            </Grid>
                            <Button variant="contained" onClick={printJournal}>{t("common.print")}</Button>
                        </Stack>
         
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Switch checked={showAddRows} onChange={(e) => setShowAddRows(e.target.checked)} />}
                        label={t("common.addLines")}
                    />
                </Grid>

                {showAddRows && (
    
                            <Stack spacing={2}>
                                <Typography variant="h6">{t("common.addLines")}</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <ItemAutoComplete
                                            key={addRequest?.itemNo}
                                            valueId={addRequest?.itemNo ?? undefined}
                                            onKeyDown={handleKeyDownAddLines}
                                            onChange={(selected) => setAddRequest(prev => prev ? { ...prev, itemNo: selected?.value ?? "" } : prev)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <VariantAutoComplete
                                            key={addRequest?.variantId}
                                            onKeyDown={handleKeyDownAddLines}
                                            itemNo={addRequest?.itemNo || ""}
                                            onChange={(selected) => setAddRequest(prev => prev ? { ...prev, variantId: selected?.id ?? null } : prev)}
                                            valueId={addRequest?.variantId}

                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label={t("common.supplierItemNo")} value={addRequest?.supplierItemNo ?? ""}
                                            onChange={(e) => setAddRequest(prev => prev ? { ...prev, supplierItemNo: e.target.value } : prev)}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            onKeyDown={handleKeyDownAddLines}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label={t("common.barcode")}
                                            value={addRequest?.barcode ?? ""}
                                            onChange={(e) => setAddRequest(prev => prev ? { ...prev, barcode: e.target.value } : prev)}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            onKeyDown={handleKeyDownAddLines}
                                        />
                                    </Grid>



                                </Grid>
                                <Button variant="contained" onClick={addLines}>{t("common.add")}</Button>
                            </Stack>
              
                )}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Switch checked={showAddFromInventory} onChange={(e) => setShowAddFromInventory(e.target.checked)} />}
                        label={t("common.addFromInventory")}
                    />
                </Grid>
                {showAddFromInventory && (

      
                            <Stack spacing={2}>
                                <Typography variant="h6">{t("common.addFromInventory")}</Typography>
                                <Grid container spacing={2}>

                            <Grid item xs={12} md={4}>
                                <LocationAutoComplete
                                    key={inventoryRequest?.location ?? null}
                                    valueId={inventoryRequest?.location ?? null}
                                    onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, location: selected?.value ?? "" } : prev)}
                                    selectFirst={true}
                                />
                            </Grid>

                                    <Grid item xs={12} md={4}>
                                        <ItemCategoryAutoComplete
                                            valueId={inventoryRequest?.itemCategory ?? undefined}
                                            onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, itemCategory: selected?.value ?? "" } : prev)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ProductGroupAutoComplete
                                            itemCategoryCode={inventoryRequest?.itemCategory ?? undefined}
                                            valueId={inventoryRequest?.productGroup ?? undefined}
                                            onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, productGroup: selected?.value ?? "" } : prev)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ItemGroupAutoComplete
                                            valueId={inventoryRequest?.itemGroup ?? undefined}
                                            onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, itemGroup: selected?.value ?? "" } : prev)}
                                        />

                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ProgramAutoComplete
                                            valueId={inventoryRequest?.program ?? undefined}
                                            onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, program: selected?.value ?? "" } : prev)}
                                        />

                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <CollectionAutoComplete
                                            valueId={inventoryRequest?.collection ?? undefined}
                                            onChange={(selected) => setInventoryRequest(prev => prev ? { ...prev, collection: selected?.value ?? "" } : prev)}
                                        />

                                    </Grid>


                                </Grid>
                                <Button variant="contained" onClick={addLinesFromInventory}>{t("common.add")}</Button>
                            </Stack>
              
                )}

         
                        <Stack spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch checked={showAdvancedControls} onChange={(e) => setShowAdvancedControls(e.target.checked)} />}
                                    label={t("common.showAdvancedControls")}
                                />
                            </Grid>
                            {showAdvancedControls && (
                                <>
                                    <Button variant="contained" fullWidth onClick={clearCopies}>{t("common.clearCopies")}</Button>
                                    <Button variant="contained" fullWidth color="error" onClick={removeAllLines}>{t("common.removeAllLines")}</Button>
                                </>
                            )}
                            <LabelJournalLines
                                key={lineRefreshKey}
                                documentNo={documentNo}
                                onLinesChanged={() => setLineRefreshKey(prev => prev + 1)}
                            />
                        </Stack>
               

                <LabelTemplateDialog open={templateDialogOpen} onClose={() => { setTemplateDialogOpen(false); fetchTemplates(); }} />
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                    <Button variant="contained" onClick={goBack}>{t("common.back")}</Button>
                </Stack>
            </Stack>
        </CenteredCard>
    );
};

export default LabelJournalEditPage;
